import classNames from "classnames";
import { StepIndicator } from "components/Auth/StepIndicator";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { CaretDown } from "components/icons/CaretDown";
import {
  CREATED_BRAND_FAILURE,
  CREATED_BRAND_SUCCESS,
  CREATED_ENTITIES_FAILURE,
  CREATED_ENTITIES_SUCCESS,
} from "constants/analyticsEvents";
import { FieldArray, Form, Formik, FormikHelpers } from "formik";
import { multiEntityCreation } from "formValidations/multiEntityCreation";
import { useAnalytics } from "hooks/useAnalytics";
import { useEntitiesIndexedDB } from "hooks/useEntitiesIndexedDB";
import { useLocalStorage } from "hooks/useLocalStorage";
import { useToast } from "hooks/useToast";
import { useUserOnboardingStepper } from "hooks/useUserOnboardingStepper";
import authContext from "jwt_context&axios/authContext";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  useCreateGroupAndEntitiesMutation,
  useGetCountriesDataQuery,
} from "store/apis/onboarding";
import { EntityBox } from "./EntityBox/EntityBox";
import { ENTITY_CREATION_TIMESTAMP } from "constants/storageKeys";
import { SmallPlusIcon } from "components/icons/export const SmallPlusIcon";
import {
  AddressFieldValues,
  EMPTY_ADDRESS_FIELDS,
} from "v2/components/AddressPicker/AddressPicker";
import { AddressData } from "v2/store/onboardingEntityApi";
import { buildEntityAddressPayload } from "util/buildEntityAddressPayload";
import { EntityCreationContext } from "./EntityCreationContext";

export type EntityCreationType = {
  groupName?: string;
  isFromAdmin?: boolean;
  moveBack?: () => void;
  onAdminSubmit?: (
    values: {
      state_id: string | null;
      formation_type_id: string;
      company_name: string;
    }[],
    files: Record<string, File>
  ) => void;
  setEntityCreate?: (val: boolean) => void;
  isCreating?: boolean;
};

export type EntityDataType = {
  country: string;
  state: string;
  formationType: string;
  entityName: string;
  suffix: string | undefined;
  showEntrySelection: boolean;
  ein: File | null;
  coiFile: File | null;
  uuid: string | undefined;
  isSaved: boolean;
  isEditing: boolean;
  dateOfIncorporation: string | null;
  registeredAgent: string | null;
  taxedAs: string;
  address: AddressFieldValues;
};

const initialEntityValue = {
  country: "",
  state: "",
  formationType: "",
  entityName: "",
  suffix: "Inc." as string | undefined,
  showEntrySelection: true,
  ein: null,
  coiFile: null,
  uuid: crypto.randomUUID(),
  isSaved: false,
  isEditing: true,
  dateOfIncorporation: null,
  registeredAgent: null,
  taxedAs: "",
  address: EMPTY_ADDRESS_FIELDS,
};

export const EntityCreationForm = (props: EntityCreationType) => (
  <EntityCreationContext.Provider
    value={{ isFromAdmin: Boolean(props.isFromAdmin) }}
  >
    <EntityCreationFormContent {...props} />
  </EntityCreationContext.Provider>
);

const EntityCreationFormContent = ({
  groupName = "",
  isFromAdmin = false,
  moveBack,
  onAdminSubmit,
  setEntityCreate,
  isCreating,
}: EntityCreationType) => {
  const { alertToast, successToast } = useToast();
  const history = useHistory();
  const { trackEvent } = useAnalytics();
  const { currentStepNumber, reset } = useUserOnboardingStepper();

  const sendToVerify = () => history.push("/verify");

  const { data: countries = [], isLoading } = useGetCountriesDataQuery();
  const [createGroupAndEntities, { isLoading: isCreatingGroup }] =
    useCreateGroupAndEntitiesMutation();

  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const auth = useContext(authContext);
  const userIdentifier =
    auth?.authtoken?.uuid || currentUser?.uuid || "default";

  const { localData: savedBrandName, removeItem: clearBrandName } =
    useLocalStorage<string>(`onboarding_brand_${userIdentifier}`, "");

  const {
    localData: savedEntityData,
    setItem: saveEntityData,
    removeItem: clearEntityData,
    isLoading: isLoadingEntityData,
  } = useEntitiesIndexedDB<EntityDataType[]>(
    `onboarding_entities_${userIdentifier}`,
    []
  );

  const usCountryId = countries.find(
    ({ code_alpha_2 }) => code_alpha_2 === "US"
  )?.uuid;

  const initialEntity = useMemo(() => initialEntityValue, []);
  const prefillEntityTypeRef = useRef(localStorage.getItem("entityType"));
  const prefillEntityType = prefillEntityTypeRef.current;

  // Use state to store initialized values
  const [initialValues, setInitialValues] = useState<{
    entityData: EntityDataType[];
  } | null>(null);

  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (!isLoadingEntityData && !isLoading && !hasInitializedRef.current) {
      let entityData: EntityDataType[];

      if (!isFromAdmin && savedEntityData && savedEntityData.length > 0) {
        entityData = savedEntityData.map((entity) => ({
          ...entity,
          uuid: entity.uuid || crypto.randomUUID(),
        }));
      } else {
        const prefill =
          prefillEntityType && usCountryId
            ? {
                country: usCountryId,
                formationType: prefillEntityType,
                showEntrySelection: false,
              }
            : {};

        entityData = [
          { ...initialEntity, uuid: crypto.randomUUID(), ...prefill },
        ];
      }

      setInitialValues({ entityData });
      hasInitializedRef.current = true;
    }
  }, [
    isLoadingEntityData,
    isLoading,
    savedEntityData,
    initialEntity,
    usCountryId,
    prefillEntityType,
    isFromAdmin,
  ]);

  const onBrandEntityCreation = async (
    values: { entityData: EntityDataType[] },
    formikHelpers: FormikHelpers<{ entityData: EntityDataType[] }>
  ) => {
    const { entityData: entitiesFromForm } = values;

    const files: Record<string, any> = {};

    const entity: {
      state_id: string | null;
      formation_type_id: string;
      company_name: string;
      ein_key: string;
      coi_key: string;
      date_of_incorporation: string | null;
      registered_agent: string | null;
      taxed_as: string | null;
      address_data: AddressData | null;
    }[] = [];

    const brandNameToUse = savedBrandName || groupName;

    const addedEntities = entitiesFromForm;

    for (let i = 0; i < addedEntities.length; i++) {
      const {
        country,
        state,
        formationType,
        entityName,
        suffix,
        ein,
        coiFile,
        dateOfIncorporation,
        registeredAgent,
        taxedAs,
        address,
      } = addedEntities[i];

      files[`ein_file${i + 1}`] = ein;
      files[`coi_file${i + 1}`] = coiFile;
      if (formationType && entityName) {
        entity.push({
          state_id: state === "" ? null : state,
          formation_type_id: formationType,
          company_name: `${entityName} ${coiFile ? "" : suffix || ""}`,
          ein_key: `ein_file${i + 1}`,
          coi_key: `coi_file${i + 1}`,
          date_of_incorporation: dateOfIncorporation,
          registered_agent: registeredAgent,
          taxed_as: taxedAs || null,
          address_data: buildEntityAddressPayload(country, address),
        });
      }
    }

    const payload = {
      group_name: brandNameToUse,
      entity_data: entity,
      ...files,
    };

    if (entity.length === 0) {
      alertToast({
        message: "Please add at least one entity with all the details.",
        position: "bottom-left",
      });
      return;
    }

    if (isFromAdmin && onAdminSubmit) {
      try {
        await onAdminSubmit(entity, files);

        reset();
      } catch (err) {
        formikHelpers.setSubmitting(false);
      }
      return;
    }

    try {
      await createGroupAndEntities({ payload }).unwrap();
      localStorage.setItem(ENTITY_CREATION_TIMESTAMP, String(Date.now()));
      setTimeout(
        () => localStorage.removeItem(ENTITY_CREATION_TIMESTAMP),
        2 * 60 * 1000
      );
      clearEntityData();
      localStorage.removeItem("entityType");
      reset();

      trackEvent(CREATED_BRAND_SUCCESS, {
        brand_name: brandNameToUse,
      });

      trackEvent(CREATED_ENTITIES_SUCCESS, {
        brand_name: brandNameToUse,
        entities: entity.map((entityData: any) => ({
          name: entityData?.name,
          country: entityData?.country,
        })),
      });

      sendToVerify();
    } catch (err: any) {
      trackEvent(CREATED_BRAND_FAILURE, {
        brand_name: brandNameToUse,
      });

      trackEvent(CREATED_ENTITIES_FAILURE, {
        brand_name: brandNameToUse,
        entities: entity.map((entityData: any) => ({
          name: entityData?.name,
          country: entityData.country,
        })),
      });
      alertToast({
        message: err?.data?.error?.message,
        position: "bottom-left",
      });
      formikHelpers.setSubmitting(false);
    }
  };

  const handleGoBack = () => {
    setEntityCreate?.(false);
    moveBack?.();
  };

  const handleDeleteEntity = (
    entityData: EntityDataType[],
    index: number,
    remove: (index: number) => void,
    push: (val: EntityDataType) => void
  ) => {
    const filteredEntity = entityData.filter((_, i) => i !== index);
    if (!isFromAdmin) saveEntityData(filteredEntity);
    remove(index);

    if (filteredEntity.length === 0) {
      push({ ...initialEntityValue, uuid: crypto.randomUUID() });
    }
  };

  const isCreatingEntity = isCreatingGroup || isCreating;

  return (
    <Async.Root
      {...{
        isLoading: isLoading || isLoadingEntityData || !initialValues,
        isEmpty: false,
        isSuccess: !isLoading && !isLoadingEntityData && !!initialValues,
      }}
    >
      <Async.Empty>
        <></>
      </Async.Empty>
      <Async.Success>
        {initialValues && (
          <Formik
            onSubmit={onBrandEntityCreation}
            validationSchema={multiEntityCreation}
            initialValues={initialValues}
            enableReinitialize={false}
            validateOnBlur={false}
          >
            {({ values, isSubmitting, errors, submitCount, setFieldValue }) => {
              const disableFinishSetup = values.entityData.some(
                (entity) => !entity.isSaved || entity.isEditing
              );

              return (
                <Form className="t-h-full">
                  <div className="all:unset t-flex t-flex-col t-h-full">
                    <div
                      className={classNames(
                        "t-flex-1 t-min-h-0 t-overflow-y-auto sm:t-min-h-fit sm:t-overflow-visible",
                        {
                          "t-pt-[72px]": !isFromAdmin,
                        }
                      )}
                    >
                      {!isFromAdmin && currentStepNumber && (
                        <>
                          <div className="t-mb-6">
                            <StepIndicator
                              currentStep={currentStepNumber}
                              totalSteps={4}
                              label="Entity details"
                            />
                          </div>
                          <div className="t-mb-4">
                            <div className="t-text-subtitle-lg t-text-text-100 t-mb-2">
                              Add entity details
                            </div>
                            <div className="t-text-body-sm t-text-text-30">
                              Add details about your entity (or entities) to
                              help us determine what applies to your brand. Both
                              U.S. and foreign entities impact your compliance
                              requirements.
                            </div>
                          </div>
                        </>
                      )}
                      <FieldArray name="entityData">
                        {({ push, remove }) => (
                          <>
                            <div className="t-flex t-flex-col t-gap-4">
                              {values.entityData.map((entity, index) => {
                                const entityError = errors?.entityData?.[index];
                                const hasError =
                                  submitCount > 0 &&
                                  entityError &&
                                  Object.keys(entityError).length > 0;

                                return (
                                  <EntityBox
                                    key={entity.uuid || `new-${index}`}
                                    entityIndex={index}
                                    onDelete={() =>
                                      handleDeleteEntity(
                                        values.entityData,
                                        index,
                                        remove,
                                        push
                                      )
                                    }
                                    onSave={(updatedEntity: EntityDataType) => {
                                      const newEntities = [
                                        ...values.entityData,
                                      ];
                                      newEntities[index] = updatedEntity;

                                      setFieldValue("entityData", newEntities);
                                      if (!isFromAdmin) {
                                        saveEntityData(newEntities);
                                      }
                                      successToast({
                                        message: "Changes saved successfully",
                                        position: "bottom-left",
                                      });
                                    }}
                                    hasError={!!hasError}
                                  />
                                );
                              })}
                              <Button
                                onClick={() => push(initialEntityValue)}
                                type="button"
                                block
                              >
                                <div className="t-w-full t-flex t-items-center t-gap-x-1">
                                  <SmallPlusIcon className="t-text-text-30" />
                                  <span className="t-text-text-60">
                                    Add another entity
                                  </span>
                                </div>
                              </Button>
                            </div>
                          </>
                        )}
                      </FieldArray>
                    </div>

                    <div
                      className={classNames(
                        "t-flex-shrink-0 sm:t-flex-auto t-pt-6 t-pb-20 sm:t-pb-6 t-flex t-items-center",
                        {
                          "t-justify-between": !isFromAdmin,
                          "t-justify-end": isFromAdmin,
                        }
                      )}
                    >
                      {!isFromAdmin && (
                        <Button
                          onClick={handleGoBack}
                          customType="link_neutral"
                          type="button"
                          size="small"
                        >
                          <CaretDown className="t-rotate-90 t-mr-1" /> Go back
                        </Button>
                      )}
                      <Button
                        customType="primary"
                        type="submit"
                        disabled={disableFinishSetup || isCreatingEntity}
                        isLoading={isCreatingEntity}
                      >
                        Finish setup
                      </Button>
                    </div>
                  </div>
                </Form>
              );
            }}
          </Formik>
        )}
      </Async.Success>
    </Async.Root>
  );
};
